import AttorneyHubAPIService from "_shared/_api/attorney-hub-api-service";
import Guid from "_shared/guids/guid";
import axios, { AxiosHeaders, CanceledError, HttpStatusCode } from "axios";
import Session from "users/__session/session";
import { ProposalJSON } from "work/proposals/_schemas/proposal-schema";
import BarebonesWorkAgreementInfoAPIResponse from "work/work-agreements/_api/responses/barebones-work-agreement-api-response";
import { WorkAgreementJSON } from "work/work-agreements/_schemas/work-agreement-schema";
import { VendorTeamJSON } from "work/work-agreements/teams/vendor-team/_schemas/vendor-team-schema";
import WorkAgreement from "work/work-agreements/work-agreement";
import PaginatedResponse from "../../../_shared/_api/pagination/responses/paginated-response";
import WorkAgreementQuery from "./queries/work-agreement-query";

export default class WorkAgreementAPIService {
  private readonly authHeaders: AxiosHeaders = new AxiosHeaders();

  private readonly session: Session;

  constructor(session: Session) {
    this.session = session;
    if (!session.authToken?.value)
      throw new Error("Session must have an authToken to create a WorkAgreementAPIService");
    this.authHeaders.set("Authorization", `Bearer ${session.authToken.value}`);
  }

  async getWorkAgreementsAsync(
    query: WorkAgreementQuery,
    abortController?: AbortController
  ): Promise<PaginatedResponse<WorkAgreementJSON>> {
    try {
      const queryString = query.searchParams.toString();
      const url = new URL(`/work/?${queryString}`, AttorneyHubAPIService.apiBaseUrl);
      const response = await axios.get(url.toString(), {
        headers: this.authHeaders,
        signal: abortController?.signal,
      });

      const paginationHeader = response.headers["x-pagination"];
      if (!paginationHeader) {
        throw new WorkAgreementAPIServiceError("getWorkAgreementsAsync", "Missing x-pagination header");
      }
      return PaginatedResponse.from<WorkAgreementJSON>(response.data, paginationHeader);
    } catch (error) {
      console.error(error);
      if (error instanceof CanceledError) throw error;
      throw new WorkAgreementAPIServiceError("getWorkAgreementsAsync", error);
    }
  }

  async getWorkAgreementByIdAsync(workId: Guid, abortController?: AbortController): Promise<WorkAgreementJSON> {
    try {
      const url = new URL(`/work/${workId.value}`, AttorneyHubAPIService.apiBaseUrl);
      const response = await axios.get(url.toString(), {
        headers: this.authHeaders,
        signal: abortController?.signal,
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.Forbidden)
        throw new InvalidWorkAgreementUserError(error.response?.data?.message, undefined, workId);
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound)
        throw new WorkAgreementNotFoundError(error.response?.data?.message, workId);

      if (error instanceof CanceledError) throw error;

      throw new WorkAgreementAPIServiceError("getWorkByIdAsync", error);
    }
  }

  async getWorkAgreementsByIds(
    ids: Guid[],
    abortController?: AbortController
  ): Promise<BarebonesWorkAgreementInfoAPIResponse[]> {
    let url = new URL("/work/work/get-by-ids", AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.post(
        url.toString(),
        ids.map((id) => id.value),
        {
          headers: this.authHeaders,
          signal: abortController?.signal,
        }
      );
      return response.data.map((workAgreementData: Partial<BarebonesWorkAgreementInfoAPIResponse>) =>
        Object.assign(new BarebonesWorkAgreementInfoAPIResponse(), workAgreementData)
      );
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.BadRequest)
        throw new InvalidWorkAgreementRequestError(error.message);
      if (error instanceof CanceledError) throw error;
      throw new WorkAgreementAPIServiceError("getWorkAgreementsByIds", error);
    }
  }

  async getWorkTeamsAsync(workId: Guid, abortController?: AbortController): Promise<VendorTeamJSON[]> {
    try {
      const url = new URL(`/work/${workId.value}/teams`, AttorneyHubAPIService.apiBaseUrl);
      const response = await axios.get(url.toString(), {
        headers: this.authHeaders,
        signal: abortController?.signal,
      });
      return response.data;
    } catch (error) {
      if (error instanceof CanceledError) throw error;
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.Forbidden)
        throw new InvalidWorkAgreementUserError(error.response?.data?.message, undefined, workId);
      throw new WorkAgreementAPIServiceError("getTeamsAsync", error);
    }
  }

  async quitWorkTeamAsync(workId: Guid, _: Session): Promise<WorkAgreementJSON> {
    try {
      const url = new URL(`/work/${workId.value}/teams/quit`, AttorneyHubAPIService.apiBaseUrl);
      const response = await axios.post(
        url.toString(),
        {},
        {
          headers: this.authHeaders,
        }
      );
      return response.data;
    } catch (error) {
      throw new WorkAgreementAPIServiceError("quitWorkTeamAsync", error);
    }
  }

  async reviseWorkAgreementAsync(workAgreement: WorkAgreement, session: Session): Promise<ProposalJSON> {
    if (!session.user) {
      throw new Error("Cannot revise work agreement without a user.");
    }
    if (!workAgreement.id) {
      throw new Error("Cannot revise work agreement without id.");
    }
    try {
      const url = new URL(`/work/${workAgreement.id.value}/revise`, AttorneyHubAPIService.apiBaseUrl);
      const response = await axios.post(url.toString(), workAgreement.id.value, {
        headers: this.authHeaders,
      });

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.Forbidden)
        throw new InvalidWorkAgreementUserError(error.response?.data?.message, undefined, workAgreement.id);
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound)
        throw new WorkAgreementNotFoundError(error.response?.data?.message, workAgreement.id);
      throw new WorkAgreementAPIServiceError("reviseWorkAgreement", error);
    }
  }

  async endWorkAgreementAsync(workAgreementId: Guid): Promise<WorkAgreementJSON> {
    if (!workAgreementId) {
      throw new Error("Cannot end work agreement without id.");
    }

    try {
      const url = new URL(`/work/${workAgreementId.value}/end-agreement`, AttorneyHubAPIService.apiBaseUrl);
      const response = await axios.post(
        url.toString(),
        {},
        {
          headers: this.authHeaders,
        }
      );
      return response.data;
    } catch (error) {
      throw new WorkAgreementAPIServiceError("endWorkAgreement", error);
    }
  }
}

export class WorkAgreementAPIServiceError extends Error {
  constructor(method: string, error: unknown) {
    super(`Error calling WorkAgreementAPIService.${method}: Error: ${error}`);
  }
}

export class InvalidWorkAgreementRequestError extends Error {
  constructor(message: string) {
    super(`Invalid Work Agreement API Request: ${message}`);
  }
}
export class WorkAgreementNotFoundError extends Error {
  workId: Guid;
  constructor(message: string, workAgreementId: Guid) {
    super(message);
    this.workId = workAgreementId;
  }
}
export class InvalidWorkAgreementUserError extends Error {
  query?: WorkAgreementQuery;
  workId?: Guid;
  constructor(message: string, query?: WorkAgreementQuery, workId?: Guid) {
    super(message);
    this.query = query;
    this.workId = workId;
  }
}
